import { Button, Container, Grid, Input, Typography } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColumns, GridRenderCellParams, GridRowParams, GridValueGetterParams } from '@mui/x-data-grid';
import { routingStore } from 'app';
import { Avatar, ConfirmDialog, DataGridStyles, Icon, Modal, SnackBarContext } from 'components';
import { DatagridPlaceholder } from 'components/datagridPlaceholder';
import { theme } from 'config';
import { observer } from 'mobx-react';
import { Company, User } from 'models';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import {  getRoutePath, Pages, RoutingParams } from 'routes';
import styled from 'styled-components';
import { ErrorMessage, ICompany, Language, UserType } from 'types';
import { caseInsensitiveComparator } from 'util/util';
import { UserForm } from 'views';


export interface ICompanyUsersProps { }

type RowData = ICompany & {users?: number};

export const CompanyUsers = observer((props: ICompanyUsersProps) => {
  const { toast } = React.useContext(SnackBarContext);
  const { t, i18n } = useTranslation();
  const routingParams: RoutingParams = useParams<RoutingParams>();
  
  const [user,         setUser]         = React.useState<User>(null);
  const [company,      setCompany]      = React.useState<Company>(null);
  const [openModal,    setOpenModal]    = React.useState<boolean>(false);
  const [confirmValue, setConfirmValue] = React.useState<string>("");
  const [openConfirmDialog,       setOpenConfirmDialog]       = React.useState<boolean>(false);
  const [openConvertConfirmation, setOpenConvertConfirmation] = React.useState<boolean>(false);
  const [confirmConvertValue,     setConfirmConvertValue]     = React.useState<string>("");
  
  const convertUserRole = async (user: User) => {
    if(user?.user_type === UserType.Participant) {
      await user.set('user_type', UserType.Manager, true);
    } else if ( user?.user_type === UserType.Manager) {
      await user.set('user_type', UserType.Participant, true);
    }
    await company.loadUsers();
  }

  React.useEffect(() => {
    Company.get(Number(routingParams.id)).then((c: Company) => setCompany(c));
  }, []);
  React.useEffect(() => {
    if(company) company.loadUsers();
  }, [company]);

  const handleContextMenu = (event: React.SyntheticEvent) => {
    event.preventDefault();
    const rowId = event.currentTarget.getAttribute("data-id");
    const path = getRoutePath(Pages.Customers, { id: "" + routingParams.id, view: 'users', subid: "" +  rowId });
    const url = window.location.origin + path;
    window.open(url, '_blank');
  }

  const columns: GridColumns = [
    {
      field:           'name',
      headerName:      t('name'),
      flex:            1,
      headerClassName: 'columnHeader',
      minWidth:        30,
      renderCell:      (params: GridRenderCellParams<string, User>) => (
        <Avatar user = {params.row} />
      ),
      sortable:        true,
      valueGetter:     (params: GridValueGetterParams) => params.row.getFullName,
      sortComparator:  caseInsensitiveComparator,
    },{
      flex:            1,
      field:           'user_type',
      headerName:      t('role'),
      headerClassName: 'columnHeader',
    },{
      field: 'action',
      type:  'actions',
      getActions: (params: GridRowParams<User>) => [
        <GridActionsCellItem
          showInMenu
          icon    = {<StyledIcon iconId='retake' size='size-18'/>}
          label   = {params.row.user_type === UserType.Participant ? t('convert_to_manager') : t('convert_to_participant')}
          onClick = {() => {setOpenConvertConfirmation(true), setUser(params.row)}}
        />,
        <GridActionsCellItem onClick={() => {setOpenConfirmDialog(true), setUser(params.row)}} icon={<StyledIcon size='size-18' iconId="bin" />} showInMenu label={t('delete')} />,
      ],
    },
  ];

  return (
    <>
      <Container maxWidth='xl'>
        <Grid pt={3} container>
          <Grid
            item
            xs             = {12}
            mb             = {3}
            display        = 'flex'
            justifyContent = 'space-between'
            alignItems     = 'center'
          >
            <Typography variant='h2'>{company?.title}</Typography>
            <Button
              variant   = 'contained'
              startIcon = {<Icon iconId='add' />}
              onClick  = {() => {
                setUser(
                    User.create({
                      title:           '',
                      first_name:      '',
                      last_name:       '',
                      personal_number: '',
                      email:           '',
                      phone:           '',
                      language:        Language.SV,
                      user_type:       UserType.Participant,
                      password:        "pw"
                    })
                  );
                  setOpenModal(true);
                }}
            >
              {t('add_users')}
            </Button>
          </Grid>
          <Modal open={openModal} onClose={() => setOpenModal(false)}>
            <Typography mb={3} variant='h3'>
              {t('add_users')}
            </Typography>
            <Grid
              container
              mb            = {4}
              rowSpacing    = {1}
              columnSpacing = {theme.columnSpacing}
            >
              <UserForm
                required
                dontSave
                hasPersonalNumber
                selectRole
                hasConfirmationButtons
                user   = {user}
                onSave = {async () => {
                  const result = await company.addUser(user);
                  if(result && result != ErrorMessage.UserExists) {
                    await company.loadUsers();
                    setOpenModal(false);
                    toast(`${t('toast_success_add_user')}!`, 'success');
                  }
                  else {
                    toast(`${t('toast_error_add_user')}, ${t(ErrorMessage.UserExists)}!`, 'error');
                  } 
                }}
                onCancel = {() => setOpenModal(false)}
              />
            </Grid>
          </Modal>
        </Grid>
        <DataGridStyles>
          <DataGrid
            autoHeight
            disableColumnSelector
            disableSelectionOnClick
            rowHeight          = {66}
            density            = 'standard'
            columns            = {columns}
            pageSize           = {25}
            components = {{
              NoRowsOverlay: () =>
              (<DatagridPlaceholder title={t('datagrid_placeholder_company_user_list')}/>)
            }}
            rows               = {[...(company?.users || [])]}
            onRowClick         = {(params: GridRowParams<RowData>) =>  
              routingStore.push(Pages.Customers, { id: "" + routingParams.id, view: 'users', subid: "" +  params.row.id })} 
            rowsPerPageOptions = {[25]}
            componentsProps={{
              row: {
                onContextMenu: handleContextMenu,
                style: { cursor: "context-menu" },
                title: t('right_click_new_tab')
              }
            }}
          />
        </DataGridStyles>
      </Container>
      <ConfirmDialog 
        content    = {<Trans 
        t          = {t}
        values     = {{user: user?.email}}
        i18nKey    = 'delete_manager_confirm_details'
        components = {{bold: <strong />}}
        />}
        disabled          = {confirmValue !== user?.email} 
        confirmInput      = {<Input value={confirmValue} onChange={(e) => setConfirmValue(e.target.value)} />}
        open              = {openConfirmDialog}
        onClose           = {() => setOpenConfirmDialog(false)}
        title             = {t('confirm_delete_manager_question') + '?'}
        confirmButtonText = {t('delete')}
        cancelButtonText  = {t('cancel')}
        onCancel          = {() => {setOpenConfirmDialog(false); setConfirmValue("")}}
        onConfirm         = {async () => {
          if (await user.remove()) {
            await company.loadUsers();
          } else {
            toast(t('toast_error_remove_manager'), 'error')
          }
          setOpenConfirmDialog(false)
          setConfirmValue("")
        }}
      />
      <ConfirmDialog 
        content    = {<Trans 
        t          = {t}
        values     = {{user: user?.email}}
        i18nKey    = {user?.user_type === UserType.Manager ? 'convert_participant_details' : 'convert_manager_details'}
        components = {{bold: <strong />}}
        />}
        disabled          = {confirmConvertValue !== user?.email} 
        confirmInput      = {<Input value={confirmConvertValue} onChange={(e) => setConfirmConvertValue(e.target.value)} />}
        open              = {openConvertConfirmation}
        onClose           = {() => setOpenConvertConfirmation(false)}
        title             = {user?.user_type === UserType.Participant ? t('confirm_convert_to_manager_question') : t('confirm_convert_to_participant_question')}
        confirmButtonText = {t('yes')}
        cancelButtonText  = {t('no')}
        onCancel          = {() => {setOpenConvertConfirmation(false); setConfirmConvertValue("")}}
        onConfirm         = {() => {convertUserRole(user); setOpenConvertConfirmation(false); setConfirmConvertValue("")}}
      />
    </>
  );
}); 

const StyledIcon = styled(Icon)`
  svg {
    color: black;
  }
`